import { getStratoLogger } from '../../../core/sdk/sdk-logger.js';
import { routesTo } from '../../validation/message-routing.js';
import {
  ValidationError,
  type ValidationErrorSource,
  type ValidationState,
  type ValidationTrigger,
  type ValidityStateKey,
} from '../../validation/types.js';
import { withSource } from '../../validation/validation-builders.js';
import { FormValidationError } from '../form-error-classes.js';
import type {
  ControlRegistrationHandle,
  FormApi,
  FormSnapshot,
  FormStatus,
  FormSubmitData,
  FormSubmitOutcome,
  FormSubmitResult,
  FormValidator,
  RegisteredControl,
} from '../form.types.js';

/**
 * Mutable config the `<Form>` component feeds into the runtime on every render.
 * @internal
 */
export interface FormRuntimeConfig<TValues = Record<string, unknown>> {
  formId?: string;
  validators: FormValidator<TValues>[];
  onSubmit?: (
    context: FormSubmitData<TValues>,
  ) => FormSubmitOutcome | Promise<FormSubmitOutcome>;
  onValidityChange?: (state: ValidationState) => void;
}

/**
 * A field error together with its provenance: `isManual` marks errors the
 * consumer supplied, whether through `form.setError()` or by returning them
 * from `onSubmit`. Those reveal immediately instead of waiting for the field to
 * be touched.
 * @internal
 */
export interface RuntimeFieldMessage {
  error: ValidationError;
  isManual: boolean;
}

/**
 * The full runtime surface: the public {@link FormApi} plus the internal hooks
 * the `<Form>` component and registered controls use.
 * @internal
 */
export interface FormRuntime<
  TValues = Record<string, unknown>,
> extends FormApi<TValues> {
  /** Registers a control and returns its imperative handle. */
  register: (control: RegisteredControl) => ControlRegistrationHandle;
  /** Subscribes to any runtime change (for `useSyncExternalStore`). */
  subscribe: (listener: () => void) => () => void;
  /** The form-wide errors currently rendered by `<FormMessages>`. */
  getFormMessages: () => ValidationError[];
  /**
   * Runtime-owned field errors rendered by `FormFieldMessages`, each paired
   * with whether the consumer supplied it, through `setError()` or an
   * `onSubmit` return, as opposed to a form-level validator or a control's own
   * validation run producing it.
   */
  getFieldMessages: (controlId: string) => RuntimeFieldMessage[];
  /**
   * Replaces the config getter that the runtime calls on each submit/validate.
   * Called by `<Form>` when an external runtime is supplied via the `form` prop.
   * Pass `undefined` to revert to the no-op default (e.g. on unmount).
   * @internal
   */
  _bindConfig?: (fn: (() => FormRuntimeConfig<TValues>) | undefined) => void;
}

type RegisteredEntry = {
  control: RegisteredControl;
  token: symbol;
};

/** State for one submit run: its abort scope and its pending outcome. */
type SubmitRun<TValues> = {
  controller: AbortController;
  /** The values read when the run started, before validation could change. */
  values: TValues;
  settle: (result: FormSubmitResult<TValues>) => void;
};

function isAbortError(value: unknown): boolean {
  return value instanceof Error && value.name === 'AbortError';
}

function isValidationErrorOutcome(
  value: unknown,
): value is ValidationError | ValidationError[] {
  return Array.isArray(value)
    ? value.length > 0 && value.every((item) => item instanceof ValidationError)
    : value instanceof ValidationError;
}

function isFormWide(error: ValidationError): boolean {
  return (
    error instanceof FormValidationError && error.formControlId === undefined
  );
}

// Ordinal comparator for building canonical, environment-independent dedup and
// signature keys. `localeCompare` is locale/ICU-dependent, so it could order
// non-ASCII ids differently across machines — these keys must be deterministic.
function compareOrdinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function errorIdentity(error: ValidationError): string {
  const target = Array.isArray(error.formControlId)
    ? [...error.formControlId].sort(compareOrdinal).join(',')
    : (error.formControlId ?? '');
  const formId = error instanceof FormValidationError ? error.formId : '';
  return `${error.validityStateKey.toString()}|${target}|${formId}`;
}

function errorSignature(error: ValidationError): string {
  return `${errorIdentity(error)}|${error.message}`;
}

function errorSetSignature(errors: ValidationError[]): string {
  return errors.map(errorSignature).sort(compareOrdinal).join('\u0000');
}

function stateIsPending(state: ValidationState): boolean {
  return state.status === 'pending';
}

const SOURCE_PRIORITY: Record<ValidationErrorSource, number> = {
  custom: 0,
  strato: 1,
  native: 2,
};

/** Resolves a racing validation run when an abort releases it. */
const CANCELLED = Symbol('cancelled');

export function createFormRuntime<TValues = Record<string, unknown>>(
  getConfig?: () => FormRuntimeConfig<TValues>,
): FormRuntime<TValues> {
  const config: FormRuntimeConfig<TValues> = { validators: [] };
  const controls = new Map<string, RegisteredEntry>();
  const fieldStates = new Map<string, ValidationState>();
  // Errors the consumer supplied through `setError` or an `onSubmit` return.
  // Held by reference so the gating rule can tell them apart from anything a
  // validator produced, which no property on the error itself records.
  let manualErrors: ValidationError[] = [];
  let formValidatorErrors: ValidationError[] = [];
  // Whether any validation run has settled. Keeps an untouched form from
  // reporting itself valid before anything has been checked.
  let hasValidated = false;
  let validationRunId = 0;
  // Set by an abort, cleared when the next run starts. Distinguishes "the user
  // cancelled" from "the form is invalid" for anyone reading `getStatus()`.
  let wasAborted = false;
  let activeRun: SubmitRun<TValues> | undefined;
  // Errors the runtime raised from an `onSubmit` return value. Tracked so a
  // later successful run can retire exactly those, without touching errors the
  // consumer set by hand.
  let reportedErrors: ValidationError[] = [];
  // Run ids currently awaiting their controls/validators. A stale run stays
  // in here until it settles, but `isPending` only consults the latest run — see
  // there for why.
  const unsettledRunIds = new Set<number>();
  // One releaser per in-flight validation run, so an abort can unblock a
  // caller even when a validator never settles.
  const validationCancels = new Map<number, () => void>();

  let readConfig = getConfig ?? (() => config);
  const listeners = new Set<() => void>();
  const warnedDuplicateNames = new Set<string>();

  let formMessagesCache: ValidationError[] = [];
  let formMessagesSignature = '';
  const fieldMessagesCache = new Map<
    string,
    { signature: string; messages: RuntimeFieldMessage[] }
  >();
  let lastValiditySignature: string | undefined;

  function notify() {
    for (const listener of listeners) {
      listener();
    }
  }

  function hasPendingFields(): boolean {
    return [...fieldStates.values()].some(stateIsPending);
  }

  function isPending(): boolean {
    // Ignore stale runs still settling in the background — only the latest
    // run's pending state should hold the form pending.
    return unsettledRunIds.has(validationRunId) || hasPendingFields();
  }

  function discardInFlightValidation() {
    // Bumping the id makes every in-flight run's result fail its `runId ===
    // validationRunId` guard, so nothing stale is written back.
    validationRunId += 1;
    unsettledRunIds.clear();
    for (const cancel of validationCancels.values()) {
      cancel();
    }
    validationCancels.clear();
  }

  /**
   * Tears down the active submit run. A supersede stays `'pending'` on the way
   * into its successor, and a reset is about to wipe everything anyway, so only
   * a real cancel reports `'aborted'`.
   */
  function abortRun(reason: 'cancelled' | 'superseded' | 'reset') {
    const run = activeRun;
    if (reason === 'cancelled') {
      if (run === undefined && !isPending()) {
        return;
      }
      wasAborted = true;
      discardInFlightValidation();
    }
    if (reason === 'reset') {
      discardInFlightValidation();
    }
    if (run !== undefined) {
      activeRun = undefined;
      run.controller.abort();
      run.settle({
        validity: 'aborted',
        snapshot: buildSnapshot(run.values),
        errors: aggregateErrors(),
      });
    }
    if (reason === 'cancelled') {
      // Kept separate from the guard above: notify() must fire after
      // activeRun is cleared and run.settle() has resolved, so subscribers
      // see the aborted state and the submit promise settles first.
      emitValidityChange('submit');
      notify();
    }
  }

  /**
   * Reconciles the errors the consumer reported by returning them from
   * `onSubmit`. Whatever the previous run raised is retired first, so a run
   * that now succeeds clears the message it put up last time.
   */
  function applyReportedErrors(outcome: FormSubmitOutcome) {
    const returned =
      outcome === undefined ? [] : Array.isArray(outcome) ? outcome : [outcome];
    if (reportedErrors.length === 0 && returned.length === 0) {
      return;
    }
    const retired = new Set(reportedErrors);
    manualErrors = manualErrors.filter((error) => !retired.has(error));
    reportedErrors = returned;
    for (const error of returned) {
      const identity = errorIdentity(error);
      manualErrors = [
        ...manualErrors.filter(
          (existing) => errorIdentity(existing) !== identity,
        ),
        error,
      ];
    }
    emitValidityChange('submit');
    notify();
  }

  function aggregateErrors(): ValidationError[] {
    const seen = new Set<string>();
    const result: ValidationError[] = [];
    const push = (error: ValidationError) => {
      const signature = errorSignature(error);
      if (!seen.has(signature)) {
        seen.add(signature);
        result.push(error);
      }
    };
    for (const state of fieldStates.values()) {
      state.errors.forEach(push);
    }
    formValidatorErrors.forEach(push);
    manualErrors.forEach(push);
    return result;
  }

  /**
   * The errors that make the form invalid. Everything a validator produced
   * counts. A consumer-supplied error counts only when it opted in through
   * `_blockSubmission`, because a backend rejection is a verdict on the attempt
   * that was sent, and the backend judges the next one again.
   */
  function gatingErrors(): ValidationError[] {
    const setByConsumer = new Set(manualErrors);
    return aggregateErrors().filter(
      (error) => !setByConsumer.has(error) || error._blockSubmission,
    );
  }

  function errorsForField(controlId: string): RuntimeFieldMessage[] {
    const matched = aggregateErrors().filter(
      (error) => matchesTarget(error, controlId) && routesTo(error, 'field'),
    );
    // So a consumer that renders only errors[0] gets a deterministic result.
    // Sort is stable, so two errors of the same source keep their relative order.
    return [...matched]
      .sort((a, b) => SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source])
      .map((error) => ({
        error,
        isManual: manualErrors.includes(error),
      }));
  }

  function buildStatus(): FormStatus {
    return {
      validity: currentValidity(),
      errors: aggregateErrors(),
    };
  }

  function currentValidity(): FormStatus['validity'] {
    if (isPending()) {
      return 'pending';
    }
    if (wasAborted) {
      return 'aborted';
    }
    // A form only claims to be valid once a run has actually checked it.
    // Before that it reports `'invalid'`, so nothing is treated as submittable
    // on the strength of an assumption.
    return hasValidated && gatingErrors().length === 0 ? 'valid' : 'invalid';
  }

  function readValues(): TValues {
    const values: Record<string, unknown> = {};
    for (const { control } of controls.values()) {
      const key = control.name ?? control.id;
      if (Object.hasOwn(values, key) && !warnedDuplicateNames.has(key)) {
        warnedDuplicateNames.add(key);
        getStratoLogger('Form').warn(
          `Multiple form controls share the name "${key}"; only the last value is kept in the form snapshot.`,
        );
      }
      values[key] = control.getValue();
    }
    return values as TValues;
  }

  /**
   * Pass `values` to report what a run submitted rather than what the controls
   * hold now. A submit reads them before validation starts, so typing during an
   * async validator cannot change what the result says was checked.
   */
  function buildSnapshot(values?: TValues): FormSnapshot<TValues> {
    const fields: FormSnapshot['fields'] = {};
    const gating = new Set(gatingErrors());
    for (const { control } of controls.values()) {
      const fieldErrors = errorsForField(control.id);
      fields[control.id] = {
        isValid: fieldErrors.every(({ error }) => !gating.has(error)),
        isTouched: fieldStates.has(control.id),
        errors: fieldErrors.map(({ error }) => error),
      };
    }
    return {
      values: values ?? readValues(),
      fields,
    };
  }

  function validationStatus(): ValidationState['status'] {
    if (isPending()) {
      return 'pending';
    }
    return wasAborted ? 'aborted' : 'done';
  }

  function emitValidityChange(trigger: ValidationTrigger) {
    const errors = aggregateErrors();
    const signature = `${currentValidity()}|${validationStatus()}|${errorSetSignature(errors)}`;
    // The runtime notifies liberally — it emits on the way into and out of every
    // validation run and on every mutation. Collapse to a signature so
    // `onValidityChange` fires only on real transitions, not identical recomputations.
    if (signature === lastValiditySignature) {
      return;
    }
    lastValiditySignature = signature;
    readConfig().onValidityChange?.({
      status: validationStatus(),
      trigger,
      errors,
    });
  }

  async function runValidation(
    trigger: ValidationTrigger,
    reveal: boolean,
    submittedValues?: TValues,
  ): Promise<FormStatus> {
    const runId = ++validationRunId;
    const runControls = [...controls.values()];
    wasAborted = false;
    unsettledRunIds.add(runId);
    // A control's `validateField` may never settle. Racing every await against
    // this lets `abortValidation()` release the caller, including a caller
    // awaiting a plain `validate()` with no submit run behind it.
    let cancelRun!: () => void;
    const cancelled = new Promise<typeof CANCELLED>((resolve) => {
      cancelRun = () => resolve(CANCELLED);
    });
    // Starting a run supersedes every older one. Release them, or a submit
    // waiting on a validator that never settles is stuck behind a run whose
    // result the id guard would have thrown away anyway.
    for (const cancel of validationCancels.values()) {
      cancel();
    }
    validationCancels.clear();
    validationCancels.set(runId, cancelRun);
    emitValidityChange(trigger);
    notify();

    try {
      const results = await Promise.race([
        Promise.all(
          runControls.map(async ({ control, token }) => {
            const state = await control.validateField({ reveal, trigger });
            return { id: control.id, token, state };
          }),
        ),
        cancelled,
      ]);
      if (results === CANCELLED) {
        return buildStatus();
      }

      if (runId === validationRunId) {
        for (const { id, token, state } of results) {
          if (controls.get(id)?.token === token) {
            fieldStates.set(id, state);
          }
        }
      }

      // A form-level validator has to judge the same values the run reports
      // it submitted, not whatever the user typed while field validation ran.
      const snapshot = buildSnapshot(submittedValues);
      const validatorResults = await Promise.race([
        Promise.all(
          readConfig().validators.map((validator) =>
            runFormValidator(validator, snapshot.values, readConfig().formId),
          ),
        ),
        cancelled,
      ]);
      if (validatorResults === CANCELLED) {
        return buildStatus();
      }

      if (runId === validationRunId) {
        formValidatorErrors = validatorResults
          .flat()
          .map((error) => withSource(error, 'custom'));
      }
    } finally {
      validationCancels.delete(runId);
      unsettledRunIds.delete(runId);
      if (runId === validationRunId) {
        hasValidated = true;
      }
      emitValidityChange(trigger);
      notify();
    }

    return buildStatus();
  }

  const api: FormRuntime<TValues> = {
    getStatus: buildStatus,
    getSnapshot: buildSnapshot,

    validate: () => runValidation('manual', true),

    submit: async (options) => {
      // A new submit supersedes the one in flight: its signal fires so the
      // consumer's work is torn down, and its `validationResult` settles as
      // `'aborted'` so nothing is left awaiting a promise that will never come.
      abortRun('superseded');

      const controller = new AbortController();
      let settleValidation!: (result: FormSubmitResult<TValues>) => void;
      let settled: FormSubmitResult<TValues> | undefined;
      const validationResult = new Promise<FormSubmitResult<TValues>>(
        (resolve) => {
          settleValidation = resolve;
        },
      );
      const run: SubmitRun<TValues> = {
        controller,
        // Read now, not when validation settles. A value typed during an async
        // validator must not change what this run reports it submitted.
        values: readValues(),
        settle: (result) => {
          if (settled !== undefined) {
            return;
          }
          settled = result;
          settleValidation(result);
        },
      };
      activeRun = run;

      const context: FormSubmitData<TValues> = {
        form: api,
        abortSignal: controller.signal,
        trigger: options?.trigger ?? 'submit',
        validationResult,
      };

      // Invoking the callback runs its synchronous prologue right now, before
      // validation starts. That window is where a consumer clears a stale
      // form-wide error so it does not gate the run about to begin.
      let callbackError: unknown;
      let callbackFailed = false;
      let callbackOutcome: FormSubmitOutcome;
      const runCallback = async () => readConfig().onSubmit?.(context);
      const callbackDone = runCallback().then(
        (outcome) => {
          callbackOutcome = outcome;
        },
        (cause: unknown) => {
          callbackFailed = true;
          callbackError = cause;
        },
      );

      const submitRunId = validationRunId + 1;
      let validationFailure: unknown;
      let validationRejected = false;
      // A control's `validateField` can throw. Capturing it here rather than
      // letting it escape keeps the race below from leaving a rejected promise
      // with no handler; it is rethrown after the callback has had its turn.
      // `onSubmit`'s synchronous prologue has already run. If it cancelled
      // this run, starting validation would clear the aborted status and write
      // results for a run nobody is waiting on.
      const validation = controller.signal.aborted
        ? Promise.resolve()
        : runValidation('submit', true, run.values).catch((cause: unknown) => {
            validationRejected = true;
            validationFailure = cause;
          });
      // An abort must not leave the caller waiting on a validator that may
      // never settle. `validationResult` is already settled by every abort
      // path, so racing against it unblocks `submit()` without caring which
      // one fired. The abandoned run keeps going; the `validationRunId` guard
      // discards whatever it eventually produces.
      await Promise.race([validation, validationResult]);

      if (validationRejected) {
        // No verdict was reached, so the run has no validity to report. It
        // still has to settle, or the consumer's `await` never returns.
        run.settle({
          validity: 'aborted',
          snapshot: buildSnapshot(run.values),
          errors: aggregateErrors(),
        });
      } else if (
        !controller.signal.aborted &&
        validationRunId === submitRunId
      ) {
        const gating = gatingErrors();
        if (gating.length > 0) {
          focusFirstInvalid(controls, gating);
        }
        run.settle({
          validity: gating.length === 0 ? 'valid' : 'invalid',
          snapshot: buildSnapshot(run.values),
          errors: aggregateErrors(),
        });
      } else {
        // A standalone `validate()` took the run id, so this run reached no
        // verdict of its own. It still has to settle, or a callback awaiting
        // `validationResult` waits forever and `submit()` waits on it.
        run.settle({
          validity: 'aborted',
          snapshot: buildSnapshot(run.values),
          errors: aggregateErrors(),
        });
      }

      try {
        await callbackDone;
        if (!controller.signal.aborted) {
          if (!callbackFailed) {
            applyReportedErrors(callbackOutcome);
          } else if (isValidationErrorOutcome(callbackError)) {
            // Rejecting with an error and returning one mean the same thing.
            // Otherwise `.catch(e => { throw toError(e); })` would show the
            // user nothing while `.catch(e => toError(e))` shows the message.
            applyReportedErrors(callbackError);
            callbackFailed = false;
          }
        }
        if (validationRejected && !controller.signal.aborted) {
          throw validationFailure;
        }
        // A cancel tears the consumer's `fetch` down, which surfaces as an
        // `AbortError`. That is the cancel working, not the submit failing, so
        // it is swallowed. Every other rejection still reaches the caller.
        if (
          callbackFailed &&
          !(controller.signal.aborted && isAbortError(callbackError))
        ) {
          throw callbackError;
        }
      } finally {
        if (activeRun === run) {
          activeRun = undefined;
        }
        notify();
      }

      if (settled === undefined) {
        // Every path above settles the run. This keeps the return type honest.
        const aborted: FormSubmitResult<TValues> = {
          validity: 'aborted',
          snapshot: buildSnapshot(run.values),
          errors: aggregateErrors(),
        };
        run.settle(aborted);
        return aborted;
      }
      if (settled.validity === 'aborted' || controller.signal.aborted) {
        // A newer submit has since moved the shared error state on. Reporting
        // it here would hand this caller the other run's verdict.
        return settled;
      }
      // Recompute now that the callback has finished. `validationResult` has to
      // settle while the callback is still running, so it cannot carry an error
      // the callback goes on to return. The value `submit()` hands back can.
      const gating = gatingErrors();
      return {
        validity: gating.length === 0 ? 'valid' : 'invalid',
        // Rebuilt, not reused: a blocking error the callback returned has to
        // show up in the field state too, and `settled.snapshot` predates it.
        snapshot: buildSnapshot(run.values),
        errors: aggregateErrors(),
      };
    },

    abortSubmission: () => {
      abortRun('cancelled');
    },

    abortValidation: () => {
      if (!isPending()) {
        return;
      }
      wasAborted = true;
      discardInFlightValidation();
      const run = activeRun;
      // The run's own work is left alone, since only validation was cancelled,
      // but its outcome still has to arrive or the consumer awaits forever.
      run?.settle({
        validity: 'aborted',
        snapshot: buildSnapshot(run.values),
        errors: aggregateErrors(),
      });
      emitValidityChange('manual');
      notify();
    },

    resetValidation: () => {
      // A reset must not emit an `'aborted'` beat on its way to wiping
      // everything; the consumer asked for a clean slate, not a cancellation.
      abortRun('reset');
      wasAborted = false;
      hasValidated = false;
      reportedErrors = [];
      manualErrors = [];
      formValidatorErrors = [];
      fieldStates.clear();
      for (const { control } of controls.values()) {
        control.resetValidation?.();
      }
      lastValiditySignature = undefined;
      emitValidityChange('manual');
      notify();
    },

    setError: (error) => {
      const identity = errorIdentity(error);
      manualErrors = [
        ...manualErrors.filter(
          (existing) => errorIdentity(existing) !== identity,
        ),
        error,
      ];
      emitValidityChange('manual');
      notify();
    },

    focusFirstInvalid: () => {
      focusFirstInvalid(controls, gatingErrors());
    },

    clearError: (target, validityStateKey) => {
      manualErrors = manualErrors.filter(
        (error) => !matchesTarget(error, target, validityStateKey),
      );
      emitValidityChange('manual');
      notify();
    },

    register: (control) => {
      // Per-registration token. A control id can be re-registered (remount, or a
      // duplicate id). Every handle captures its token and no-ops if the live entry's
      // token differs, so a stale handle's async report/unregister can't clobber the
      // current registration.
      const token = Symbol(control.id);
      controls.set(control.id, { control, token });
      notify();
      return {
        reportValidity: (state) => {
          if (controls.get(control.id)?.token !== token) {
            return;
          }
          // The form moved on, so a cancelled run is no longer what its state
          // describes. Without this, every keystroke after a cancel would
          // keep reporting `'aborted'` until the next full run.
          wasAborted = false;
          if (state.trigger === 'change') {
            // A field-scoped `setError` error is the backend's verdict on the
            // value that was sent. Editing the field retires that verdict. A
            // form-wide error has no input to edit, so it survives.
            manualErrors = manualErrors.filter(
              (error) => isFormWide(error) || !matchesTarget(error, control.id),
            );
          }
          const prev = fieldStates.get(control.id);
          fieldStates.set(
            control.id,
            state.status === 'pending' && prev
              ? { ...state, errors: prev.errors }
              : state,
          );
          emitValidityChange(state.trigger);
          notify();
        },
        unregister: () => {
          if (controls.get(control.id)?.token !== token) {
            return;
          }
          controls.delete(control.id);
          fieldStates.delete(control.id);
          emitValidityChange('manual');
          notify();
        },
      };
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    // `FormMessages`/`FormFieldMessages` read these via `useSyncExternalStore`, which
    // compares snapshots by reference. Return the cached array unless the error set's
    // signature actually changed — a fresh array on every read forces a re-render and
    // can loop.
    getFormMessages: () => {
      // Form-wide errors: the routing subclass, plus any control's native/Strato
      // error explicitly routed to the form outlet via `_messageTarget`.
      const current = aggregateErrors().filter(
        (error) =>
          error instanceof FormValidationError || routesTo(error, 'form'),
      );
      const signature = errorSetSignature(current);
      if (signature !== formMessagesSignature) {
        formMessagesSignature = signature;
        formMessagesCache = current;
      }
      return formMessagesCache;
    },

    getFieldMessages: (controlId) => {
      const current = errorsForField(controlId);
      const signature = `${errorSetSignature(
        current.map(({ error }) => error),
      )}|${current.map(({ isManual }) => (isManual ? '1' : '0')).join('')}`;
      const cached = fieldMessagesCache.get(controlId);
      if (cached?.signature === signature) {
        return cached.messages;
      }
      fieldMessagesCache.set(controlId, { signature, messages: current });
      return current;
    },

    _bindConfig: (fn) => {
      readConfig = fn ?? (() => config);
    },
  };

  return api;
}

async function runFormValidator<TValues>(
  validator: FormValidator<TValues>,
  values: TValues,
  formId: string | undefined,
): Promise<ValidationError[]> {
  try {
    return await validator({ values });
  } catch (cause) {
    if (formId !== undefined) {
      return [
        new FormValidationError('Validation failed.', {
          formId,
          cause,
        }),
      ];
    }
    return [
      new ValidationError('Validation failed.', { source: 'custom', cause }),
    ];
  }
}

function matchesTarget(
  error: ValidationError,
  target: string,
  validityStateKey?: ValidityStateKey,
): boolean {
  let routedTo: boolean;
  if (error instanceof FormValidationError) {
    routedTo = error.formId === target;
    if (!routedTo && error.formControlId !== undefined) {
      routedTo = Array.isArray(error.formControlId)
        ? error.formControlId.includes(target)
        : error.formControlId === target;
    }
  } else {
    routedTo = Array.isArray(error.formControlId)
      ? error.formControlId.includes(target)
      : error.formControlId === target;
  }
  if (!routedTo) {
    return false;
  }
  return (
    validityStateKey === undefined ||
    error.validityStateKey.equals(validityStateKey)
  );
}

function focusFirstInvalid(
  controls: Map<string, RegisteredEntry>,
  errors: ValidationError[],
) {
  if (errors.length === 0) {
    return;
  }
  const invalidIds = new Set(
    errors.flatMap((error) =>
      error.formControlId === undefined
        ? []
        : Array.isArray(error.formControlId)
          ? error.formControlId
          : [error.formControlId],
    ),
  );
  if (invalidIds.size === 0) {
    // All errors are form-level only (formId, no formControlId). There is no
    // specific control to target — the error is shown in FormMessages and no
    // focus is moved.
    return;
  }
  for (const { control } of controls.values()) {
    if (invalidIds.has(control.id)) {
      control.focus();
      return;
    }
  }
}
